import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  createQuestionaireRequest,
  updateQuestionaireRequest,
} from "../../../Apis/questionaire";
import useUser from "../../../customHooks/useUser";
import { updateUserTypeField } from "../../../authentication/actions";
import {
  editQuestionaire,
  openQuestionaire,
  fetchUserTypes,
  setSelectedUserType,
  createUserQuestionnaire,
  updateUserQuestionnaire,
} from "../../../actions";
import { fetchToken } from "../../../authentication/actions";

const useQuestionaire = () => {
  const { uuid, questionaireId, csrfToken, userType, isLoggedIn } = useUser();
  const isEdit = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.isEdit
  );
  const isOpen = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.isOpen
  );
  const questionnaireStructure = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.structure
  );
  const businessRepresentativeOptions = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.businessRepresentativeOptions
  );
  const userTypes = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.userTypes
  );
  const userTypesLoading = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.userTypesLoading
  );
  const selectedUserType = useSelector(
    ({ userQuestionaire }) => userQuestionaire?.selectedUserType
  );

  const showQuestionaire = (userType === null && uuid && isLoggedIn) || isOpen;

  const dispatch = useDispatch();

  // Fetch user types on mount if not already loaded
  useEffect(() => {
    if (!userTypes && !userTypesLoading) {
      console.log("📋 Fetching user types on mount");
      dispatch(fetchUserTypes());
    }
  }, [dispatch, userTypes, userTypesLoading]);

  // Fetch CSRF token if missing
  useEffect(() => {
    if (!csrfToken && isLoggedIn) {
      console.log("🔐 Fetching CSRF token");
      dispatch(fetchToken());
    }
  }, [dispatch, csrfToken, isLoggedIn]);

  // CREATE QUESTIONAIRE
  function questionaireMutationCreate(questionnaire) {
    const userTypeId = findUserTypeId(selectedUserType || "CORPORATE");
    const data = questionaireJsonApiRequest(
      questionnaire,
      uuid,
      null,
      userTypeId
    );
    return createQuestionaireRequest(csrfToken, data, uuid);
  }

  const createQuestionaire = useMutation(questionaireMutationCreate, {
    onSuccess: (response) => {
      const { id } = response.data;
      // Update user type locally without making /user/user call
      dispatch(
        updateUserTypeField({
          userType: selectedUserType || "CORPORATE",
          questionaireId: id,
        })
      );
      handleQuestionaire("CLOSE");
    },
  });

  // UPDATE QUESTIONAIRE
  function questionaireMutationUpdate(questionnaire) {
    const userTypeId = findUserTypeId(selectedUserType || "CORPORATE");
    const data = questionaireJsonApiRequest(
      questionnaire,
      uuid,
      questionaireId,
      userTypeId
    );
    return updateQuestionaireRequest(csrfToken, data, questionaireId, uuid);
  }

  const updateQuestionaire = useMutation(questionaireMutationUpdate, {
    onSuccess: async () => {
      await handleQuestionaire("CLOSE");
    },
  });

  // Create a mock questionnaire object for editing based on the structure
  const questionaire = {
    data: isEdit
      ? {
          data: {
            attributes: {
              // Default values based on questionnaire structure
              field_legal_name: "",
              field_tax_id: "",
              field_representative_name: "",
              field_representative_number: "",
              field_representative_email: "",
              field_website: "",
              field_business_activity_code: "",
              field_has_ibank: false,
              field_uses_erp: "",
              field_erp_solution_name: "",
              field_erp_solution_version: "",
              field_banking_activities: "",
            },
            relationships: {
              field_product_categories: {
                data: [],
              },
            },
          },
        }
      : null,
    isLoading: false,
    error: null,
  };

  // Simplified flow: Just fetch user types and let user select
  const fetchAndSelectUserType = async (userTypeLabel) => {
    dispatch(setSelectedUserType(userTypeLabel));
    dispatch(fetchUserTypes());
  };

  // Create questionnaire after user type selection
  const createQuestionnaireForUserType = (userTypeLabel, formData = {}) => {
    console.log(
      "🎯 Hook: createQuestionnaireForUserType called with:",
      userTypeLabel,
      formData,
      "userTypes:",
      userTypes,
      "csrfToken:",
      csrfToken ? "Present" : "Missing"
    );

    // Don't proceed if userTypes haven't loaded yet
    if (!userTypes || userTypes.length === 0) {
      console.error("❌ Cannot create questionnaire: User types not loaded");
      return;
    }

    // Don't proceed if CSRF token is missing
    if (!csrfToken) {
      console.error("❌ Cannot create questionnaire: CSRF token missing");
      dispatch(fetchToken()); // Try to fetch it
      return;
    }

    const userTypeId = findUserTypeId(userTypeLabel);
    console.log(
      "🔍 Found userTypeId:",
      userTypeId,
      "for label:",
      userTypeLabel
    );

    // Map form fields to API fields
    // The Core stores and validates product category ids (the same GUIDs Drupal, the product
    // catalogue and the migrated questionnaires use), so send the selected ids as a
    // comma-separated list - never the display names.
    const categoryIds = Array.isArray(formData.field_product_categories)
      ? formData.field_product_categories.filter(Boolean).join(",")
      : "";

    const mappedData = {
      title: formData.title || `Questionnaire for user ${uuid}`,
      productCategories: categoryIds,
      companyLegalName: formData.field_company_legal_name || "",
      companyTaxNumber: formData.field_company_tax_id || "",
      companyRepFullName: formData.field_company_repr_full_name || "",
      companyRepNumber: formData.field_company_repr_number || "",
      companyReprEmail: formData.field_company_repr_email || "",
      companyWebsite: formData.field_company_website || "",
      companyKad: formData.field_company_kad || "",
      companyOwnsBankAccount: formData.field_company_owns_bank_account || false,
      companyUsesErp: formData.field_company_uses_erp === true,
      companyErpSolutionName: formData.field_company_erp_solution_name || "",
      companyErpSolutionVersion:
        formData.field_company_erp_solution_versi || "",
      erpBankingActivities: formData.field_erp_banking_activities || "",
      consentInformation:
        formData.consent !== undefined ? formData.consent : true,
    };

    const payload = {
      userTypeId,
      ...mappedData,
      // For DEVELOPER (freelancer), override with minimal data
      ...(userTypeLabel === "DEVELOPER"
        ? {
            companyLegalName: "",
            companyTaxNumber: "",
            companyWebsite: "",
            companyKad: "",
            companyOwnsBankAccount: false,
            companyRepFullName: "",
            companyRepNumber: "",
            companyReprEmail: "",
            companyUsesErp: false,
            companyErpSolutionName: "",
            companyErpSolutionVersion: "",
            erpBankingActivities: "",
            productCategories: "",
          }
        : {}),
    };

    console.log(
      "🚀 Hook: Dispatching createUserQuestionnaire with payload:",
      payload
    );
    dispatch(createUserQuestionnaire(payload));
  };

  // Update existing questionnaire
  const updateQuestionnaireForUserType = (userTypeLabel, formData = {}) => {
    console.log(
      "📝 Hook: updateQuestionnaireForUserType called with:",
      userTypeLabel,
      formData
    );

    // Map form fields to API fields
    // Convert product category IDs to names and join as comma-separated string
    const categoryNames = Array.isArray(formData.field_product_categories)
      ? formData.field_product_categories
          .map((categoryId) => {
            const category = businessRepresentativeOptions.find(
              (opt) => opt.id === categoryId
            );
            return category ? category.name : categoryId; // Use name if found, otherwise keep ID as fallback
          })
          .join(", ")
      : "";

    const mappedData = {
      title: formData.title || `Questionnaire for user ${uuid}`,
      productCategories: categoryNames,
      companyLegalName: formData.field_company_legal_name || "",
      companyTaxNumber: formData.field_company_tax_id || "",
      companyRepFullName: formData.field_company_repr_full_name || "",
      companyRepNumber: formData.field_company_repr_number || "",
      companyReprEmail: formData.field_company_repr_email || "",
      companyWebsite: formData.field_company_website || "",
      companyKad: formData.field_company_kad || "",
      companyOwnsBankAccount: formData.field_company_owns_bank_account || false,
      companyUsesErp: formData.field_company_uses_erp === true,
      companyErpSolutionName: formData.field_company_erp_solution_name || "",
      companyErpSolutionVersion:
        formData.field_company_erp_solution_versi || "",
      erpBankingActivities: formData.field_erp_banking_activities || "",
      consentInformation:
        formData.consent !== undefined ? formData.consent : true,
    };

    const userTypeId = findUserTypeId(userTypeLabel);
    const payload = {
      id: questionaireId,
      userTypeId,
      ...mappedData,
    };

    console.log(
      "📝 Hook: Dispatching updateUserQuestionnaire with payload:",
      payload
    );
    dispatch(updateUserQuestionnaire(payload));
  };

  const findUserTypeId = (userTypeLabel) => {
    if (!userTypes || userTypes.length === 0) {
      return userTypeLabel; // fallback to original value if no data
    }

    // Handle case-insensitive matching
    // UI sends "DEVELOPER" or "CORPORATE", API has "Developer" or "Corporate"
    const userType = userTypes.find(
      (type) => type.name?.toUpperCase() === userTypeLabel?.toUpperCase()
    );

    return userType ? userType.id : userTypeLabel;
  };

  const handleQuestionaire = async (action) => {
    switch (action) {
      case "CLOSE":
        if (isEdit && userType) {
          dispatch(editQuestionaire(false));
        } else if (isOpen) {
          dispatch(openQuestionaire(false));
        } else {
          dispatch(updateUserTypeField({ userType: "CLOSED" }));
        }
        break;
      case "OPEN":
        if (userType) {
          dispatch(editQuestionaire(true));
        } else {
          dispatch(openQuestionaire(true));
        }
        break;
      default:
        return;
    }
  };

  return {
    isEdit,
    showQuestionaire,
    createQuestionaire,
    updateQuestionaire,
    questionaire,
    handleQuestionaire,
    fetchAndSelectUserType,
    createQuestionnaireForUserType,
    updateQuestionnaireForUserType,
    questionnaireStructure,
    businessRepresentativeOptions,
    userTypes,
    userTypesLoading,
    selectedUserType,
  };
};

export default useQuestionaire;

const questionaireJsonApiRequest = (
  data,
  userId,
  questionaireId,
  userTypeId
) => {
  const {
    consent,
    field_product_categories,
    field_company_legal_name,
    field_company_tax_id,
    field_company_repr_full_name,
    field_company_repr_number,
    field_company_repr_email,
    field_company_website,
    field_company_kad,
    field_company_owns_bank_account,
    field_company_uses_erp,
    field_company_erp_solution_name,
    field_company_erp_solution_versi,
    field_erp_banking_activities,
  } = data;

  return {
    ...(questionaireId && { id: questionaireId }),
    title: `Questionaire for user ${userId}`,
    langcode: "en",
    status: true,
    sticky: false,
    promote: false,
    published: true,
    companyLegalName: field_company_legal_name || "",
    companyTaxNumber: field_company_tax_id || "",
    companyWebsite: field_company_website || "",
    companyKad: field_company_kad || "",
    companyOwnsBankAccount: field_company_owns_bank_account || false,
    companyRepFullName: field_company_repr_full_name || "",
    companyRepNumber: field_company_repr_number || "",
    companyReprEmail: field_company_repr_email || "",
    companyUsesErp: field_company_uses_erp === true,
    companyErpSolutionName: field_company_erp_solution_name || "",
    companyErpSolutionVersion: field_company_erp_solution_versi || "",
    erpBankingActivities: field_erp_banking_activities || "",
    consentInformation: consent || false,
    corporateUserId: userId,
    productCategories: Array.isArray(field_product_categories)
      ? field_product_categories
      : "",
    userTypeId: userTypeId || "CORPORATE",
  };
};
